import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, Settings, FileText, MessageSquare, Archive, Shield, Moon, Sun } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../utils/cn';
import { isAdmin } from '../utils/roles';

export function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navigation = [
    { name: 'Αρχική', href: '/', current: false },
    { name: 'Περιστατικά', href: '/cases', current: false },
    { name: 'Αρχείο', href: '/archive', current: false },
    { name: 'Φόρουμ', href: '/forum', current: false },
  ];

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-primary-200 dark:border-gray-700 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-medical-500 to-primary-600 rounded-xl flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-lg">🔬</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-xl text-primary-900 dark:text-white tracking-tight">Pathiety</span>
                <span className="text-xs text-primary-500 dark:text-gray-400 font-medium -mt-1">Παθολογική Ανατομική</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  item.current
                    ? 'border-medical-500 text-primary-900 dark:text-white'
                    : 'border-transparent text-primary-600 hover:border-primary-300 hover:text-primary-800 dark:text-gray-300 dark:hover:text-white',
                  'inline-flex items-center px-1 pt-1 border-b-2 text-medical-sm font-medium transition-colors duration-200'
                )}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right side buttons */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {/* Action buttons */}
                <div className="hidden sm:flex items-center space-x-2">
                  <Link
                    to="/cases/new"
                    className="inline-flex items-center px-3 py-2 border border-transparent text-medical-sm font-medium rounded-lg text-white bg-gradient-to-r from-medical-500 to-medical-600 hover:from-medical-600 hover:to-medical-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-medical-500 shadow-sm transition-all duration-200"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Νέο Περιστατικό
                  </Link>
                  <Link
                    to="/forum/create"
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-medical-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-all duration-200"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Νέα Δημοσίευση
                  </Link>
                </div>

                {/* User menu */}
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {user?.avatar_url ? (
                      <img
                        className="w-8 h-8 rounded-full"
                        src={user.avatar_url}
                        alt={`${user.firstName} ${user.lastName}`}
                      />
                    ) : (
                      <div className="w-8 h-8 bg-primary-200 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-primary-600" />
                      </div>
                    )}
                    <span className="hidden sm:block text-primary-700 dark:text-gray-300 font-medium text-medical-sm">
                      {user?.firstName} {user?.lastName}
                    </span>
                  </button>

                  {/* User dropdown menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4 mr-2" />
                        Προφίλ
                      </Link>
                      
                      {/* Admin Panel Link - Only for admins */}
                      {user && isAdmin(user.role) && (
                        <>
                          <div className="border-t border-gray-100 my-1"></div>
                          <Link
                            to="/admin"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 bg-red-50 dark:text-gray-300 dark:hover:bg-gray-700 dark:bg-red-900/20"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Shield className="w-4 h-4 mr-2 text-red-600" />
                            <span className="font-medium text-red-700">Πίνακας Διαχείρισης</span>
                          </Link>
                          <div className="border-t border-gray-100 my-1"></div>
                        </>
                      )}
                      
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Ρυθμίσεις
                      </Link>
                      
                      {/* Theme Toggle */}
                      <button
                        onClick={() => {
                          toggleTheme();
                          setIsUserMenuOpen(false);
                        }}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        {theme === 'dark' ? (
                          <>
                            <Sun className="w-4 h-4 mr-2" />
                            Φωτεινό Θέμα
                          </>
                        ) : (
                          <>
                            <Moon className="w-4 h-4 mr-2" />
                            Σκοτεινό Θέμα
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Αποσύνδεση
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="text-primary-600 hover:text-primary-800 px-3 py-2 rounded-lg text-medical-sm font-medium transition-colors duration-200"
                >
                  Σύνδεση
                </Link>
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-medical-500 to-medical-600 hover:from-medical-600 hover:to-medical-700 text-white px-4 py-2 rounded-lg text-medical-sm font-medium shadow-sm transition-all duration-200"
                >
                  Εγγραφή
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center p-3 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 touch-manipulation min-h-[44px] min-w-[44px]"
              onClick={toggleMobileMenu}
            >
              <span className="sr-only">Άνοιγμα κύριου μενού</span>
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 relative z-50">
            <div className="pt-2 pb-3 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    item.current
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700',
                    'block pl-3 pr-4 py-3 border-l-4 text-base font-medium min-h-[44px] flex items-center touch-manipulation'
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {isAuthenticated && (
                <>
                  <Link
                    to="/cases/new"
                    className="block pl-3 pr-4 py-3 border-l-4 border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 text-base font-medium min-h-[44px] flex items-center touch-manipulation"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FileText className="w-4 h-4 inline mr-2" />
                    Νέο Περιστατικό
                  </Link>
                  <Link
                    to="/forum/create"
                    className="block pl-3 pr-4 py-3 border-l-4 border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 text-base font-medium min-h-[44px] flex items-center touch-manipulation"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <MessageSquare className="w-4 h-4 inline mr-2" />
                    Νέα Δημοσίευση
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>


      {/* Close user menu when clicking outside */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}
    </header>
  );
}